import React, { Fragment, useLayoutEffect } from 'react';

import useEnhancedTranslation from 'lib/useEnhancedTranslation';
import useUBTTracker from 'lib/useUBTTracker';
import { getPublicPath } from 'lib/utils';
import I18n from 'components/I18n';
import Box from 'components/react-styled-core/Box';
import Image from 'components/react-styled-core/Image';
import Text from 'components/react-styled-core/Text';

import Container from './components/Container';
import Header from './components/Header';
import Logo from './components/Logo';
import LanguageDropdown from './components/LanguageDropdown';
import Content from './components/Content';
import IntroSection from './components/IntroSection';
import Section from './components/Section';
import PageTitle from './components/PageTitle';
import Description from './components/Description';
import ServiceTitle from './components/ServiceTitle';
import ItemLink from './components/ItemLink';
import Note from './components/Note';
import Link from './components/Link';

const mapLanguageToPath = (lang) => {
    return (
        {
            en: 'en-us',
            it: 'en-us',
            pt: 'pt-br',
            es: 'es-es',
            el: 'en-us',
        }
    )[lang] || '';
};

const GetStartedWithTM = ({ history }) => {
    const { trackEvent } = useUBTTracker({ defaultCategory: 'Quick Start Guide' });
    const i18n = useEnhancedTranslation();
    const { i18next } = i18n;
    const languageInPath = mapLanguageToPath(i18next.language);

    const publicPath = getPublicPath();
    useLayoutEffect(() => {
        history.push(`/getting-started?lang=${i18next.language}`); // change url
        // eslint-disable-next-line
    }, [i18next.language]);

    return (
        <Fragment>
            <Header>
                <Container>
                    <Logo />
                    <LanguageDropdown lang={i18next.language} onSelect={lang => i18next.changeLanguage(lang)} />
                </Container>
            </Header>
            <Content>
                <Container>
                    <IntroSection>
                        <PageTitle>{i18n._('Getting Started with Trend Micro')}</PageTitle>
                        <Description lineHeight="base">
                            {i18n._('The following sections point you to getting started topics that can help you get your protection set up according to your own needs. Depending on your Trend Micro package, any or all of the following products may be available to protect your endpoints, online collaboration applications, and email accounts. If you chose Worry-Free Services Advanced or Worry-Free Services XDR, you automatically receive all of the products below.')}
                        </Description>
                    </IntroSection>
                    <Section>
                        <Image
                            src={`${publicPath}images/img_wfbs.svg`}
                            alt={i18n._('Worry-Free Services')}

                            verticalAlign="top"
                            display={['block', 'inline-block']}
                            ml={['auto', '0']}
                            mr={['auto', '8x']}
                        />
                        <Box
                            display={['block', 'inline-block']}
                            width={['100%', 'calc(100% - 228px)']}
                        >
                            <ServiceTitle>{i18n._('Worry-Free Services')}</ServiceTitle>
                            <Description
                                pt="2x" // 8px
                                pb="4x" // 16px
                            >
                                {i18n._('Access your Worry-Free Security Services console to start installing Security Agents on your Windows, Mac, Android, and iOS devices to protect yourself from ransomware and advanced malware attacks. Start your endpoint protection by performing the following:')}
                            </Description>
                            <ItemLink
                                itemNo={1}
                                href={`https://docs.trendmicro.com/${languageInPath}/smb/worry-free-business-security-services-67-server-help/security-agent-insta_001.aspx`}
                                onClick={() => trackEvent('link.agent-installation')}
                            >
                                {i18n._('Install the Security Agent on your endpoint')}
                            </ItemLink>
                            <ItemLink
                                itemNo={2}
                                href={`https://docs.trendmicro.com/${languageInPath}/smb/worry-free-business-security-services-67-server-help/policy-management_001.aspx`}
                                onClick={() => trackEvent('link.configuring-policy')}
                            >
                                {i18n._('Review or change your default policy settings')}
                            </ItemLink>
                        </Box>
                    </Section>
                    <Section>
                        <Image
                            src={`${publicPath}images/img_cas.svg`}
                            alt={i18n._('Cloud Apps Security')}

                            verticalAlign="top"
                            display={['block', 'inline-block']}
                            ml={['auto', '0']}
                            mr={['auto', '8x']}
                        />
                        <Box
                            display={['block', 'inline-block']}
                            width={['100%', 'calc(100% - 228px)']}
                        >
                            <ServiceTitle>{i18n._('Cloud Apps Security')}</ServiceTitle>
                            <Description
                                pt="2x"
                                pb="4x"
                            >
                                {i18n._('Cloud App Security can seamlessly protect your cloud collaboration services after you set up the services.')}
                            </Description>
                            <Text
                                display="block"
                                fontWeight="bold"
                                fontSize="lg"
                                py="2x"
                            >
                                {i18n._('Email Cloud Apps')}
                            </Text>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/cloud-app-security/getstarted/provisioning-for-mic"
                                remark={i18n._('*Requires the Global Admin account')}
                                onClick={() => trackEvent('link.provisioning-for-mic')}
                            >
                                {i18n._('Set up Exchange Online (Office 365)')}
                            </ItemLink>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/enterprise/cloud-app-security-online-help/getting-started/provisioning-a-servi.aspx"
                                remark={i18n._('*Requires the Super Admin account')}
                                onClick={() => trackEvent('link.provisioning-g-suite')}
                            >
                                {i18n._('Set up Gmail (G Suite)')}
                            </ItemLink>
                            <Note pb="6x">
                                {i18n._('If someone else set up your account for you, contact them for help.')}
                            </Note>
                            <Text
                                display="block"
                                fontWeight="bold"
                                fontSize="lg"
                                pb="2x"
                            >
                                {i18n._('File Sharing and Storage Apps')}
                            </Text>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/cloud-app-security/getstarted/provisioning-box"
                                onClick={() => trackEvent('link.provisioning-box')}
                            >
                                {i18n._('Set up Box')}
                            </ItemLink>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/cloud-app-security/getstarted/provisioning-dropbox"
                                onClick={() => trackEvent('link.provisioning-dropbox')}
                            >
                                {i18n._('Set up Dropbox')}
                            </ItemLink>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/cloud-app-security/getstarted/provisioning-sharepoint"
                                onClick={() => trackEvent('link.provisioning-sharepoint')}
                            >
                                {i18n._('Set up SharePoint Online / OneDrive for Business')}
                            </ItemLink>
                            <ItemLink
                                href="https://docs.trendmicro.com/en-us/cloud-app-security/getstarted/provisioning-googledrive"
                                onClick={() => trackEvent('link.provisioning-googledrive')}
                            >
                                {i18n._('Set up Google Drive')}
                            </ItemLink>
                            <Text>
                                <I18n>
                                    After you have set up your cloud service protection, go to your
                                    {' '}
                                    <Link
                                        href="https://docs.trendmicro.com/en-us/enterprise/cloud-app-security-online-help/advanced-threat-prot_001.aspx"
                                        onClick={() => trackEvent('link.advanced-threat-policy')}
                                    >
                                        Advanced Threat Protection
                                    </Link>
                                    {' '}
                                    screen to set up your security policies.
                                </I18n>
                            </Text>
                        </Box>
                    </Section>
                    <Section>
                        <Image
                            src={`${publicPath}images/img_tmes.svg`}
                            alt={i18n._('Trend Micro Email Security Advance')}

                            verticalAlign="top"
                            display={['block', 'inline-block']}
                            ml={['auto', '0']}
                            mr={['auto', '8x']}
                        />
                        <Box
                            display={['block', 'inline-block']}
                            width={['100%', 'calc(100% - 228px)']}
                        >
                            <ServiceTitle>{i18n._('Trend Micro Email Security Advance')}</ServiceTitle>
                            <Description
                                pt="2x"
                                pb="2x"
                            >
                                {i18n._('For customers with a self-hosted email server, set up Email Security to begin protecting your messages from phishing, ransomware, and fraud attacks.')}
                            </Description>
                            <Note pb="4x">
                                {i18n._('If you do not host your own email server, you do not require this service.')}
                            </Note>
                            <ItemLink
                                itemNo={1}
                                href="https://docs.trendmicro.com/en-us/enterprise/trend-micro-email-security-online-help/getting-started-with/provisioning-a-trend"
                                onClick={() => trackEvent('link.provisioning-business-account')}
                            >
                                {i18n._('Complete the Trend Micro Business Account provisioning wizard')}
                            </ItemLink>
                            <ItemLink
                                itemNo={2}
                                href="https://docs.trendmicro.com/en-us/enterprise/trend-micro-email-security-online-help/domain-management"
                                onClick={() => trackEvent('link.domain-management')}
                            >
                                {i18n._('Set up your email domain')}
                            </ItemLink>
                            <ItemLink
                                itemNo={3}
                                href="https://docs.trendmicro.com/en-us/enterprise/trend-micro-email-security-online-help/configuring-policies"
                                onClick={() => trackEvent('link.configuring-policies')}
                            >
                                {i18n._('Review or change your default policy settings')}
                            </ItemLink>
                            <ItemLink
                                itemNo={4}
                                href="https://docs.trendmicro.com/en-us/enterprise/trend-micro-email-security-online-help/faqs-and-instruction/about-mx-records-and"
                                onClick={() => trackEvent('link.mx-records')}
                            >
                                {i18n._('Point your MX record to Email Security')}
                            </ItemLink>
                        </Box>
                    </Section>
                </Container>
            </Content>
        </Fragment>
    );
};

export default GetStartedWithTM;
